import {observable, action, flow} from 'mobx';
import {ownRequest, ownUploadFilePUT} from '../api/ownFetch'
import Task from './Task';
import notificationsStore from './NotificationsStore';
import localizationStore from './LocalizationStore';
import Categories, {addAgentToCategory, getCategories, removeAgentFromCategory} from './Categories'

class Agent {
    constructor(agent) {
        this.id = agent.id
        this.description = agent.description
        this.greetingMessage = agent.greetingMessage
        this.status = agent.status
        this.userId = agent.agentsUser.id
        this.email = agent.agentsUser.email
        this.colour = agent.colour

        Reflect.ownKeys(agentCharacteristicTypes).forEach(fieldName => {
            if (agent[fieldName]) {
                const list = agent[fieldName]
                list.forEach(char => {
                    this.characteristics.push({
                        ...char,
                        type: agentCharacteristicTypes[fieldName]
                    })
                })
            }
        })

        this.setTasks(agent.agentTasks)
        this.setSubscriptionPlans(agent.agentSubscriptionPlans)
        this.getAvatarRequest()
    }

    @observable
    description = ''

    @observable
    status = ''

    @observable.shallow
    tasks = []

    @observable.shallow
    agentSubscriptionPlans = []

    @observable
    avatar = null

    @observable.shallow
    colour = null

    @observable
    characteristics = []

    categories = new Categories()

    @action
    setDescription(d) {
        this.changeAgentPropertyRequest({'description': d})
            .then(data => this.description = d)
    }

    @action
    setStatus(s) {
        this.changeAgentPropertyRequest({'status': s})
            .then(data => this.status = s)
    }

    @action
    setAgentSubscriptionPlans(agentSubscriptionPlans) {
        this.changeAgentPropertyRequest({})
            .then(data => this.agentSubscriptionPlans = agentSubscriptionPlans)
    }

    @action
    updateAgentData(data) {
        for (let key in data) {
            if (key in this) {
                this[key] = data[key]
            }
        }
    }

    @action.bound
    setProperties = flow(function* setProperties(agentData) {
        const response = yield this.changeAgentPropertyRequest(agentData)
        const data = yield response.json()
        this.updateAgentData(data.agentData)
    })

    @action
    setAvatar(a) {
        this.avatar = a
    }

    @action
    setTasks(tasks) {
        this.tasks = []
        tasks.forEach(t => this.tasks.push(new Task(t, this)))
    }

    @action
    addTask(task){
        this.tasks.push(new Task(task, this))
    }

    @action
    deleteTask(id){
        this.tasks.splice(this.tasks.findIndex(t => t.id === id), 1)
    }

    @action
    setSubscriptionPlans(agentSubscriptionPlans) {
        this.agentSubscriptionPlans = agentSubscriptionPlans
    }

    @action
    addCharacteristic(agentCharacteristic) {
        this.removeCharacteristic(agentCharacteristic.id)
        this.characteristics.push(agentCharacteristic)
    }

    @action
    removeCharacteristic(id) {
        const index = this.characteristics.findIndex(char => char.id === id)
        if (index !== -1) {
            this.characteristics.splice(index, 1)
        }
    }

    async getAvatarRequest(){
        let response = await ownRequest('GET', '/users/' + this.userId + '/profileThumbnail')
        switch (response.status){
            case 200: {
                let data = await response.blob()
                try {
                    this.setAvatar(URL.createObjectURL(data))
                } catch (e) {
                    console.error('Wrong response data format')
                }
                return Promise.resolve()
            }
            default: {
                return error()
            }
        }
    }

    async changeAgentPropertyRequest (agentData) {
        let response = await ownRequest(
            'PUT',
            '/agentdata/' + this.id,
            undefined, // acceptType
            undefined, // params
            {agentData: agentData} // body
        );
        switch (response.status){
            case 200: {
                notificationsStore.pushNotification(localizationStore.locale.agent_prop_changed)
                return Promise.resolve(response)
            }
            default: {
                return error()
            }
        }
    }

    async changeAvatarRequest (image) {
        let response = await ownUploadFilePUT(
            {
                _links: [{
                    href: '/users/' + this.userId + '/profileThumbnail',
                    rel: 'data'
                }]
            },
            'application/vnd.uberblik.user+json', 'data', image
        )
        switch (response.status) {
            case 201: {
                try {
                    this.setAvatar(URL.createObjectURL(image))
                    notificationsStore.pushNotification(localizationStore.locale.avatar_changed)
                } catch (err) {
                    console.log(err)
                }
                return Promise.resolve()
            }
            default: {
                return error()
            }
        }
    }

    async deleteTaskRequest (taskId) {
        let response = await ownRequest(
            'DELETE',
            '/agentdata/' + this.id + '/agenttasks/' + taskId,
        )
        switch (response.status){
            case 200: {
                this.deleteTask(taskId)
                notificationsStore.pushNotification(localizationStore.locale.task_removed)
                return Promise.resolve()
            }
            default: {
                return error()
            }
        }
    }

    async createTaskRequest(agentTask) {
        let response = await ownRequest(
            'POST',
            '/agentdata/' + this.id + '/agenttasks/configuration',
            undefined, // acceptType
            undefined, // params
            {agentTask: agentTask} // body
        )
        switch (response.status){
            case 200: {
                let data = await response.json()
                try {
                    this.addTask(data.agentTask);
                    notificationsStore.pushNotification(localizationStore.locale.task_created)
                } catch (e) {
                    console.error('Wrong response data format')
                }
                return Promise.resolve()
            }
            default: {
                return error()
            }
        }
    }

    async addCharacteristicRequest(agentCharacteristic) {
        const response = await ownRequest(
          'POST',
          '/agentdata/' + this.id + '/agentcharacteristics',
          'application/vnd.uberblik.agentcharacteristic+json;charset:UTF-8', // acceptType
          undefined, // params
          {agentCharacteristic} // body
        )
        switch (response.status){
            case 200: {
                let data = await response.json()
                try {
                    this.addCharacteristic(data.agentCharacteristic);
                    notificationsStore.pushNotification(localizationStore.locale.characteristic_added)
                } catch (e) {
                    console.error('Wrong response data format')
                }
                return Promise.resolve()
            }
            default: {
                return error()
            }
        }
    }

    async removeCharacteristicRequest(characteristicID) {
        const response = await ownRequest(
          'DELETE',
          '/agentdata/' + this.id + '/agentcharacteristics/' + characteristicID
        )
        switch (response.status){
            case 200: {
                this.removeCharacteristic(characteristicID);
                notificationsStore.pushNotification(localizationStore.locale.characteristic_removed)
                return Promise.resolve()
            }
            default: {
                return error()
            }
        }
    }

    async editCharacteristicRequest(id, agentCharacteristic) {
        const response = await ownRequest(
          'PATCH',
          '/agentdata/' + this.id + '/agentcharacteristics/' + id,
          'application/vnd.uberblik.agentCharacteristic+json;charset:UTF-8', // acceptType
          undefined, // params
          {agentCharacteristic} // body
        )
        switch (response.status){
            case 200: {
                let data = await response.json()
                try {
                    if (agentCharacteristic.index === data.agentCharacteristic.index) {
                        this.addCharacteristic(data.agentCharacteristic);
                    } else {
                        const response = await getUpdatedCharacteristics(this.id, data.agentCharacteristic.type)
                        if (response.status === 200) {
                            const data = await response.json()
                            data.agentCharacteristics.forEach(char => {
                                this.addCharacteristic(char)
                            })
                        } else {
                            throw Error()
                        }
                    }
                } catch (e) {
                    console.error('Wrong response data format')
                }
                notificationsStore.pushNotification(localizationStore.locale.characteristic_changed)
                return Promise.resolve()
            }
            default: {
                return error()
            }
        }
    }

    async uploadLargeLogoRequest(file, type, colourScheme) {
        const response = await ownUploadFilePUT({
            _links: [{
                href: '/agentdata/' + this.id + '/logo',
                rel: 'data'
            }]
        }, '*/*', 'data', file, {type, colourScheme})
        switch(response.status) {
            case 201: {
                notificationsStore.pushNotification(localizationStore.locale.logo_uploaded)
                return Promise.resolve()
            }
            default: {
                return error()
            }
        }
    }

    async getLargeLogoRequest(type, colourScheme) {
        const response = await ownRequest('GET', '/agentdata/' + this.id + '/logo', undefined, {type, colourScheme})
        if (response.status === 200) {
            return await response.blob()
        }
        return null
    }

    async deleteLargeLogoRequest(type, colourScheme) {
        const response = await ownRequest('DELETE', '/agentdata/' + this.id + '/logo', undefined, {type, colourScheme})
        switch(response.status) {
            case 200: {
                notificationsStore.pushNotification(localizationStore.locale.logo_deleted)
                return Promise.resolve()
            }
            default: {
                return error()
            }
        }
    }

    async getAgentsCategoriesRequest() {
        const categories = await getCategories(this.id)
        if (categories) {
            this.categories.setCategories(categories)
        }
    }

    async addCategory(category) {
        if (await addAgentToCategory(category, this.id)) {
            this.categories.addCategory(category)
            notificationsStore.pushNotification(localizationStore.locale.success)
            return Promise.resolve()
        } else {
            return error()
        }
    }

    async removeCategory(category) {
        if (await removeAgentFromCategory(category, this.id)) {
            this.categories.removeCategory(category)
            notificationsStore.pushNotification(localizationStore.locale.success)
            return Promise.resolve()
        } else {
            return error()
        }
    }
}

async function error(localeKey = 'unknown_error') {
    notificationsStore.pushNotification(localizationStore.locale[localeKey])
    return Promise.reject()
}

async function getUpdatedCharacteristics(agentID, type) {
    return await ownRequest(
      'GET',
      '/agentdata/' + agentID + '/agentcharacteristics',
      'application/vnd.uberblik.agentCharacteristic+json;charset:UTF-8', // acceptType
      {type})
}

export const agentCharacteristicTypes = {
    experiences: 'EXPERIENCE',
    skills: 'SKILL',
    missions: 'MISSION',
    recentUpdates: 'RECENT_UPDATE',
    nextUpdates: 'NEXT_UPDATE'
}

export default Agent;



// WEBPACK FOOTER //
// ./src/store/Agent.js